import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { normalizeModelParams } from '../utils/requestNormalization';
import { buildExampleCurl, buildLoadableParams } from '../utils/restExamples';

const CodePanel = ({
  model,
  params,
  endpoint,
  activeTabId,
  category,
  style,
  onLoadExample,
}) => {
  const [activeTab, setActiveTab] = useState('curl');
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [panelMode, setPanelMode] = useState('quick');
  const [activeExampleId, setActiveExampleId] = useState(null);

  // 模型切换时重置面板状态，避免停留在新模型不支持的 tab 上
  const modelId = model?.id || model?.model_name;
  useEffect(() => {
    setPanelMode('quick');
    setActiveExampleId(null);
  }, [modelId]);

  if (!model) {
    return <div className='playground-code-panel' style={style}></div>;
  }

  const resolvedExamples = model.rest_examples_resolved || [];
  const activeExample =
    resolvedExamples.find((item) => item.id === activeExampleId) ||
    resolvedExamples[0] ||
    null;
  const modeCount =
    1 + (resolvedExamples.length > 0 ? 1 : 0) + (model.documentation ? 1 : 0);

  // 使用传入的endpoint，如果没有则使用model的endpoint
  const currentEndpoint = endpoint || model.endpoint;

  // 判断是否是查询类型的tab（优先用 activeTabId，兜底用 endpoint URL）
  const isQueryEndpoint =
    activeTabId === 'query' ||
    activeTabId === 'subject-query' ||
    activeTabId === 'subtitle-erase-query' ||
    activeTabId === 'image-upscale-query' ||
    activeTabId === 'video-upscale-query' ||
    (activeTabId == null &&
      currentEndpoint != null &&
      currentEndpoint.includes('result'));

  // 判断是否是主体创建 tab
  const isSubjectCreateEndpoint = activeTabId === 'subject-create';

  // 判断是否是主体列表 tab
  const isSubjectListEndpoint = activeTabId === 'subject-list';

  // 判断是否是素材管理 tab
  const isAssetTab = [
    'asset-group-create',
    'asset-create',
    'asset-group-get',
    'asset-get',
    'asset-group-list',
    'asset-group-update',
    'asset-list',
    'asset-update',
  ].includes(activeTabId);

  // 判断是否���字幕擦除提交 tab
  const isSubtitleEraseTab = activeTabId === 'subtitle-erase';

  // 根据endpoint类型生成默认的代码模板
  const getDefaultCodeTemplates = () => {
    if (isQueryEndpoint || isAssetTab || isSubtitleEraseTab) {
      // 查询类型 / 素材管理类型的代码模板
      return {
        curl: `curl --location '{{baseUrl}}{{endpoint}}' \\
--header 'Authorization: Bearer {{apiKey}}' \\
--header 'Content-Type: application/json' \\
--data '{{requestBody}}'`,
        python: `import requests

response = requests.post(
    '{{baseUrl}}{{endpoint}}',
    headers={'Authorization': 'Bearer {{apiKey}}'},
    json={{requestBody}}
)
print(response.json())`,
      };
    }
    // 默认使用模型的代码模板
    return model.code_templates || {};
  };

  const codeTemplates = getDefaultCodeTemplates();

  const normalizeParamsForDisplay = (rawParams) => {
    if (!model) {
      return rawParams;
    }

    return normalizeModelParams(rawParams, model, { strict: false });
  };

  const getParamsForCode = () => {
    const normalized = normalizeParamsForDisplay(params);

    if (isSubjectCreateEndpoint) {
      // 主体创建：构建 model_parameters 结构
      const body = { model: normalized.model };
      const mp = {};
      if (normalized.element_name) mp.element_name = normalized.element_name;
      if (normalized.element_description)
        mp.element_description = normalized.element_description;
      if (normalized.reference_type)
        mp.reference_type = normalized.reference_type;
      if (
        normalized.reference_type === 'image_refer' &&
        normalized.frontal_image
      ) {
        mp.element_image_list = { frontal_image: normalized.frontal_image };
        if (normalized.refer_images) {
          try {
            const parsed =
              typeof normalized.refer_images === 'string'
                ? JSON.parse(normalized.refer_images)
                : normalized.refer_images;
            if (Array.isArray(parsed) && parsed.length > 0) {
              mp.element_image_list.refer_images = parsed;
            }
          } catch (e) {
            /* ignore */
          }
        }
      }
      if (normalized.reference_type === 'video_refer' && normalized.video_url) {
        mp.element_video_list = { video_url: normalized.video_url };
      }
      if (normalized.element_voice_id)
        mp.element_voice_id = normalized.element_voice_id;
      if (normalized.tag_list) {
        try {
          const parsed =
            typeof normalized.tag_list === 'string'
              ? JSON.parse(normalized.tag_list)
              : normalized.tag_list;
          if (Array.isArray(parsed) && parsed.length > 0) {
            mp.tag_list = parsed;
          }
        } catch (e) {
          /* ignore */
        }
      }
      body.model_parameters = mp;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    if (isSubjectListEndpoint) {
      // 主体列表：model, type, pageNum, pageSize
      const body = { model: normalized.model };
      body.type = normalized.type || 'Custom-Elements';
      if (normalized.pageNum) body.pageNum = normalized.pageNum;
      if (normalized.pageSize) body.pageSize = normalized.pageSize;
      return body;
    }

    if (isAssetTab) {
      // 素材管理 tab：按 activeTabId 构建精确 body
      const body = { model: normalized.model };
      const addByPass = () => {
        if (
          normalized.byPass &&
          typeof normalized.byPass === 'object' &&
          !Array.isArray(normalized.byPass) &&
          Object.keys(normalized.byPass).length > 0
        ) {
          body.byPass = normalized.byPass;
        }
      };
      if (activeTabId === 'asset-group-create') {
        if (normalized.name) body.name = normalized.name;
        if (normalized.description) body.description = normalized.description;
        if (normalized.group_type) body.group_type = normalized.group_type;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      } else if (activeTabId === 'asset-create') {
        if (normalized.group_id) body.group_id = normalized.group_id;
        if (normalized.url) body.url = normalized.url;
        body.asset_type = normalized.asset_type || 'Image';
        if (normalized.name) body.name = normalized.name;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      } else if (activeTabId === 'asset-group-get') {
        if (normalized.id) body.id = normalized.id;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      } else if (activeTabId === 'asset-get') {
        if (normalized.id) body.id = normalized.id;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      } else if (activeTabId === 'asset-group-list') {
        if (normalized.filters) {
          try {
            body.filters =
              typeof normalized.filters === 'string'
                ? JSON.parse(normalized.filters)
                : normalized.filters;
          } catch {
            body.filters = normalized.filters;
          }
        }
        body.page_number = normalized.page_number
          ? Number(normalized.page_number)
          : 1;
        body.page_size = normalized.page_size
          ? Number(normalized.page_size)
          : 10;
        if (normalized.sort_by) body.sort_by = normalized.sort_by;
        if (normalized.sort_order) body.sort_order = normalized.sort_order;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      } else if (activeTabId === 'asset-group-update') {
        if (normalized.id) body.id = normalized.id;
        if (normalized.name) body.name = normalized.name;
        if (normalized.description) body.description = normalized.description;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      } else if (activeTabId === 'asset-list') {
        if (normalized.filters) {
          try {
            body.filters =
              typeof normalized.filters === 'string'
                ? JSON.parse(normalized.filters)
                : normalized.filters;
          } catch {
            body.filters = normalized.filters;
          }
        }
        body.page_number = normalized.page_number
          ? Number(normalized.page_number)
          : 1;
        body.page_size = normalized.page_size
          ? Number(normalized.page_size)
          : 10;
        if (normalized.sort_by) body.sort_by = normalized.sort_by;
        if (normalized.sort_order) body.sort_order = normalized.sort_order;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      } else if (activeTabId === 'asset-update') {
        if (normalized.id) body.id = normalized.id;
        if (normalized.name) body.name = normalized.name;
        if (normalized.project_name)
          body.project_name = normalized.project_name;
        addByPass();
      }
      return body;
    }

    if (isSubtitleEraseTab) {
      return {
        model: normalized.model || 'Doubao-subtitle-erase',
        video_url: normalized.video_url || '',
      };
    }

    if (isQueryEndpoint) {
      // 查询类 endpoint 只需要 model、task_id，以及非空的 byPass
      const filtered = {};
      if (normalized.model !== undefined) filtered.model = normalized.model;
      if (normalized.task_id !== undefined && normalized.task_id !== '')
        filtered.task_id = normalized.task_id;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        filtered.byPass = normalized.byPass;
      }
      return filtered;
    }

    // 生成类：移除默认空值的可选字段，避免冗余
    if (Array.isArray(normalized.images) && normalized.images.length === 0) {
      delete normalized.images;
    }
    if (
      normalized.byPass &&
      typeof normalized.byPass === 'object' &&
      !Array.isArray(normalized.byPass) &&
      Object.keys(normalized.byPass).length === 0
    ) {
      delete normalized.byPass;
    }

    return normalized;
  };

  const generateCode = (template) => {
    if (!template) return '';

    const baseUrl = window.location.origin;
    const apiKey = 'YOUR_API_KEY';
    const requestBody = JSON.stringify(getParamsForCode(), null, 2);

    return template
      .replace(/\{\{baseUrl\}\}/g, baseUrl)
      .replace(/\{\{endpoint\}\}/g, currentEndpoint)
      .replace(/\{\{apiKey\}\}/g, apiKey)
      .replace(/\{\{requestBody\}\}/g, requestBody);
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    // 可以添加一个提示
  };

  const tabs = [];
  if (codeTemplates?.curl) tabs.push({ key: 'curl', label: 'cURL' });
  if (codeTemplates?.python) tabs.push({ key: 'python', label: 'Python' });
  if (codeTemplates?.javascript)
    tabs.push({ key: 'javascript', label: 'JavaScript' });
  if (model.response_example)
    tabs.push({ key: 'response', label: '请求/响应示例' });

  return (
    <div className='playground-code-panel' style={style}>
      {modeCount > 1 && (
        <div className='code-tabs code-mode-tabs'>
          <div
            className={`code-tab ${panelMode === 'quick' ? 'active' : ''}`}
            onClick={() => setPanelMode('quick')}
          >
            快速调用
          </div>
          {resolvedExamples.length > 0 && (
            <div
              className={`code-tab ${panelMode === 'examples' ? 'active' : ''}`}
              onClick={() => setPanelMode('examples')}
            >
              调用示例
            </div>
          )}
          {model.documentation && (
            <div
              className={`code-tab ${panelMode === 'docs' ? 'active' : ''}`}
              onClick={() => setPanelMode('docs')}
            >
              模型文档
            </div>
          )}
        </div>
      )}

      {panelMode === 'examples' && (
        <div className='rest-examples-panel'>
          {resolvedExamples.length === 0 ? (
            <div className='code-block'>
              <div className='rest-example-empty-title'>请选择一个调用示例</div>
              <div className='rest-example-empty-text'>
                这里提供适合当前模型的 REST 请求模板，可一键复制后直接接入业务。
              </div>
            </div>
          ) : (
            <div className='rest-examples-layout'>
              <div className='rest-example-list-column'>
                <div className='rest-example-list'>
                  {resolvedExamples.map((example) => (
                    <div
                      key={example.id}
                      className={`rest-example-item ${
                        activeExample?.id === example.id ? 'active' : ''
                      }`}
                      onClick={() => setActiveExampleId(example.id)}
                    >
                      <div className='rest-example-title'>{example.name}</div>
                      <div className='rest-example-summary'>
                        {example.summary}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className='rest-example-detail-column'>
                {activeExample && (
                  <div className='code-block-wrapper'>
                    <div className='rest-example-actions'>
                      <button
                        className='code-copy-button'
                        onClick={() =>
                          handleCopy(
                            buildExampleCurl(activeExample, {
                              baseUrl: window.location.origin,
                              apiKey: 'YOUR_API_KEY',
                              modelId: model.id,
                            })
                          )
                        }
                      >
                        复制 REST 示例
                      </button>
                      {activeExample.loadable &&
                        typeof onLoadExample === 'function' && (
                          <button
                            className='code-copy-button'
                            onClick={() =>
                              onLoadExample(
                                buildLoadableParams(
                                  activeExample.request_body,
                                  {
                                    model: model.id,
                                  }
                                )
                              )
                            }
                          >
                            载入到参数面板
                          </button>
                        )}
                    </div>
                    <div className='code-block'>
                      <div className='rest-example-detail-header'>
                        <div className='rest-example-title'>
                          {activeExample.name}
                        </div>
                        <div className='rest-example-summary'>
                          {activeExample.summary}
                        </div>
                      </div>
                      <pre>
                        {buildExampleCurl(activeExample, {
                          baseUrl: window.location.origin,
                          apiKey: 'YOUR_API_KEY',
                          modelId: model.id,
                        })}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {panelMode === 'quick' && tabs.length > 0 && (
        <>
          <div className='code-tabs'>
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`code-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </div>
            ))}
          </div>
          <div className='code-content'>
            {tabs.map((tab) => {
              if (activeTab !== tab.key) return null;

              // 请求/响应示例Tab
              if (tab.key === 'response') {
                // 优先使用 request_example，如果没有则回退到 base_params
                const requestExample =
                  model.request_example || model.base_params || {};
                const combinedExample = `请求示例：\n${JSON.stringify(
                  requestExample,
                  null,
                  2
                )}\n\n响应示例：\n${JSON.stringify(
                  model.response_example,
                  null,
                  2
                )}`;
                return (
                  <div key={tab.key} className='code-block-wrapper'>
                    <button
                      className='code-copy-button'
                      onClick={() => handleCopy(combinedExample)}
                    >
                      复制
                    </button>
                    <div className='code-block'>
                      <div style={{ marginBottom: '20px' }}>
                        <div
                          style={{
                            color: '#888',
                            marginBottom: '8px',
                            fontWeight: 'bold',
                          }}
                        >
                          请求示例：
                        </div>
                        <pre>{JSON.stringify(requestExample, null, 2)}</pre>
                      </div>
                      <div>
                        <div
                          style={{
                            color: '#888',
                            marginBottom: '8px',
                            fontWeight: 'bold',
                          }}
                        >
                          响应示例：
                        </div>
                        <pre>
                          {JSON.stringify(model.response_example, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                );
              }

              // 代码模板Tab
              const code = generateCode(codeTemplates[tab.key]);
              return (
                <div key={tab.key} className='code-block-wrapper'>
                  <button
                    className='code-copy-button'
                    onClick={() => handleCopy(code)}
                  >
                    复制
                  </button>
                  <div className='code-block'>
                    <pre>{code}</pre>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {panelMode === 'docs' && model.documentation && (
        <div
          className='code-documentation code-documentation-clickable'
          onClick={() => setDocModalOpen(true)}
          title='点击查看完整文档'
        >
          <div className='code-doc-title'>
            文档
            <span className='code-doc-expand-hint'>点击展开</span>
          </div>
          <div className='code-doc-content'>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {model.documentation}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* 文档弹窗 */}
      {docModalOpen && (
        <div
          className='doc-modal-overlay'
          onClick={() => setDocModalOpen(false)}
        >
          <div className='doc-modal' onClick={(e) => e.stopPropagation()}>
            <div className='doc-modal-header'>
              <span className='doc-modal-title'>文档</span>
              <button
                className='doc-modal-close'
                onClick={() => setDocModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className='doc-modal-body'>
              <div className='code-doc-content'>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {model.documentation}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodePanel;
